import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';

const OAuth2RedirectHandler = () => {
    const navigate = useNavigate();
    const { loginSuccess } = useAuth();

    const handleDirectLogin = useCallback(async (token) => {
        const success = await loginSuccess(token);
        
        if (success) {
            const savedRedirect = localStorage.getItem('redirectAfterLogin');
            
            if (savedRedirect) {
                localStorage.removeItem('redirectAfterLogin');
                navigate(savedRedirect);
            } else {
                navigate('/');
            }
        } else {
            navigate('/', { 
                state: { 
                    error: 'Authentication failed' 
                } 
            });
        }
    }, [loginSuccess, navigate]);

    useEffect(() => {
        const getUrlParameter = (name) => {
            name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
            const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
            const results = regex.exec(window.location.search);
            const value = results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
            return value;
        };

        const token = getUrlParameter('token');
        const error = getUrlParameter('error');

        if (error) {
            console.error("Authentication error:", error);
            
            if (window.opener) {
                try {
                    window.opener.postMessage({
                        type: 'oauth2_error',
                        error: error,
                        message: 'Authentication failed'
                    }, window.location.origin);
                    
                    window.close();
                } catch (err) {
                    console.error('Error sending message to opener:', err);
                    navigate('/', { 
                        state: { 
                            error: 'Authentication failed', 
                            errorDetails: error 
                        } 
                    });
                }
            } else {
                navigate('/', { 
                    state: { 
                        error: 'Authentication failed', 
                        errorDetails: error 
                    } 
                });
            }
            return;
        }

        if (token) {
            if (window.opener) {
                try {
                    window.opener.postMessage({
                        type: 'oauth2_success',
                        token: token
                    }, window.location.origin);
                    
                    window.close();
                } catch (err) {
                    console.error('Error sending message to opener:', err);
                    handleDirectLogin(token);
                }
            } else {
                handleDirectLogin(token);
            }
        } else {
            console.error("No token found in URL");
            navigate('/', { 
                state: { 
                    error: 'No authentication token received' 
                } 
            });
        }
    }, [navigate, loginSuccess, handleDirectLogin]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh'
        }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>
                Processing authentication...
            </Typography>
        </Box>
    );
};

export default OAuth2RedirectHandler;